import React, { useState } from 'react';
import { Typography, Container, Box, Button, Paper, Grid } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import englishManual from '../assets/usermanual/english_manual.pdf';
import frenchManual from '../assets/usermanual/french_manual.pdf';
import chineseManual from '../assets/usermanual/chinese_manual.pdf';
import { useLanguage } from '../LanguageContext';
import { translations } from '../translations';
import PdfViewerModal from '../components/PdfViewerModal';

const ManualView = () => {
    const { language } = useLanguage();
    const t = translations[language].manual;
    const [modalOpen, setModalOpen] = useState(false);

    // Select the appropriate PDF based on language
    const getUserManualPdf = () => {
        switch (language) {
            case 'fr':
                return frenchManual;
            case 'zh':
                return chineseManual;
            default:
                return englishManual;
        }
    };

    const handleOpenPdf = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    return (
        <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 100px)', py: 6, px: 2 }}>
            <Container maxWidth="md" sx={{ px: { xs: 2, sm: 2 } }}>
                <Typography
                    variant="h3"
                    component="h1"
                    gutterBottom
                    align="center"
                    sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}
                >
                    {t.title}
                </Typography>
                <Typography
                    variant="h6"
                    align="center"
                    color="text.secondary"
                    sx={{ mb: 6, fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                    {t.subtitle}
                </Typography>

                {/* Mobile App Section */}
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        mb: 4,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                        color: 'white'
                    }}
                >
                    <Typography variant="h3" sx={{ fontSize: '2.5rem', mb: 1 }}>
                        📱
                    </Typography>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                        {t.mobileAppTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                        {t.mobileAppDescription}
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={() => window.open('https://apps.apple.com/app/thermopied/id6471418961', '_blank')}
                            sx={{
                                px: 2.5,
                                py: 1,
                                bgcolor: 'white',
                                color: '#000',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s'
                            }}
                        >
                            🍎 {t.appStore}
                        </Button>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={() => window.open('https://play.google.com/store/apps/details?id=com.thermo.myapplication', '_blank')}
                            sx={{
                                px: 2.5,
                                py: 1,
                                bgcolor: 'white',
                                color: '#000',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                textTransform: 'none',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s'
                            }}
                        >
                            📱 {t.googlePlay}
                        </Button>
                    </Box>
                </Paper>

                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                        color: 'white',
                        mb: 4
                    }}
                >
                    <PictureAsPdfIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                        {t.pdfTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                        {t.pdfDescription}
                    </Typography>
                    <Button
                        variant="contained"
                        size="medium"
                        onClick={handleOpenPdf}
                        startIcon={<PictureAsPdfIcon />}
                        sx={{
                            px: 3,
                            py: 1,
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            bgcolor: 'white',
                            color: 'primary.main',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.9)',
                                transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        {t.download}
                    </Button>
                </Paper>

                <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%' }}>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Paper elevation={2} sx={{ p: 3, width: '100%' }}>
                            <Typography variant="h6" gutterBottom fontWeight={600} color="primary" sx={{ textAlign: 'left' }}>
                                📖 {t.whatsIncluded}
                            </Typography>
                            {t.included.map((item, index) => (
                                <Typography key={index} variant="body2" paragraph={index < t.included.length - 1} sx={{ textAlign: 'left' }}>
                                    ✓ {item}
                                </Typography>
                            ))}
                        </Paper>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Paper elevation={2} sx={{ p: 3, width: '100%' }}>
                            <Typography variant="h6" gutterBottom fontWeight={600} color="primary" sx={{ textAlign: 'left' }}>
                                🛠 {t.troubleshooting}
                            </Typography>
                            {t.troubleshootingItems.map((item, index) => (
                                <Typography key={index} variant="body2" paragraph={index < t.troubleshootingItems.length - 1} sx={{ textAlign: 'left' }}>
                                    ✓ {item}
                                </Typography>
                            ))}
                        </Paper>
                    </Grid>
                </Grid>

            </Container>

            {/* PDF Modal */}
            <PdfViewerModal
                open={modalOpen}
                onClose={handleCloseModal}
                pdfUrl={getUserManualPdf()}
                title={t.pdfTitle}
            />
        </Box>
    );
};

export default ManualView;